import React, { useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import {
  monthYearLabel,
  fullDateLabel,
  monthlyTicks,
  computeCandleColors,
  computeCandleMedSeries,
} from "../../lib/indicators";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";

const RANGE_OPTIONS = [
  { value: "ALL", days: 1500 },
  { value: "12M", days: 252 },
  { value: "6M", days: 126 },
];

const COLORS = {
  green: "oklch(0.52 0.17 145)",
  red: "oklch(0.50 0.22 25)",
  blue: "oklch(0.55 0.17 240)",
  black: "oklch(0.18 0 0)",
  gray: "oklch(0.552 0.016 285.938)",
};

// Default recharts scatter radius is ~5; 30% smaller ≈ 3.5
const MARKER_RADIUS = 3.5;

const SmallDot = (props) => {
  const { cx, cy, fill } = props;
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={MARKER_RADIUS} fill={fill} />;
};

const FIELD_LABEL = {
  open: "Abertura",
  high: "Máxima",
  low: "Mínima",
  close: "Fechamento",
};

export default function ColorizationChart({
  history,
  currency = "USD",
  range = "ALL",
  field = "close",
  N = 20,
}) {
  const [showMedLine, setShowMedLine] = useState(true);

  const nClamped = Math.max(1, Math.min(550, Number.isFinite(+N) ? Math.round(+N) : 20));

  const { rows, ticks, yDomain } = useMemo(() => {
    if (!history || history.length === 0) {
      return { rows: [], ticks: [], yDomain: ["auto", "auto"] };
    }

    // Compute colours & med series on the FULL history (so causality is intact
    // regardless of how the user slices by period), then slice at the end.
    const colors = computeCandleColors(history, nClamped, "sma");
    const med = computeCandleMedSeries(history, nClamped);

    const fullRows = history.map((d, j) => ({
      j,
      raw_date: d.quote_date,
      open: +Number(d.open).toFixed(4),
      high: +Number(d.high).toFixed(4),
      low: +Number(d.low).toFixed(4),
      close: +Number(d.close).toFixed(4),
      line_value: +Number(d[field]).toFixed(4),
      med: med[j] != null ? +med[j].toFixed(4) : null,
      med_prev: med[j - 1] != null ? +med[j - 1].toFixed(4) : null,
      hi_jm2: j >= 2 ? +Number(history[j - 2].high).toFixed(4) : null,
      low_jm2: j >= 2 ? +Number(history[j - 2].low).toFixed(4) : null,
      color: colors[j], // null | "green" | "blue" | "red"
      // Convenience fields for the Scatter components (null when color !== that bucket)
      marker_green: colors[j] === "green" ? +Number(d.low).toFixed(4) : null,
      marker_blue: colors[j] === "blue" ? +Number(d.low).toFixed(4) : null,
      marker_red: colors[j] === "red" ? +Number(d.low).toFixed(4) : null,
    }));

    const rangeDays = RANGE_OPTIONS.find((o) => o.value === range)?.days || 1500;
    const start = Math.max(0, fullRows.length - rangeDays);
    const sliced = fullRows.slice(start);

    // Dynamic Y axis — min/max of visible candles + ±5%
    let vmin = Infinity;
    let vmax = -Infinity;
    for (const r of sliced) {
      for (const v of [r.low, r.high, r.line_value, r.med]) {
        if (v == null || !Number.isFinite(v)) continue;
        if (v < vmin) vmin = v;
        if (v > vmax) vmax = v;
      }
    }
    let dom;
    if (!Number.isFinite(vmin) || !Number.isFinite(vmax) || vmin === vmax) {
      dom = ["auto", "auto"];
    } else {
      dom = [+(Math.max(0, vmin * 0.95)).toFixed(2), +(vmax * 1.05).toFixed(2)];
    }

    return { rows: sliced, ticks: monthlyTicks(sliced), yDomain: dom };
  }, [history, range, field, nClamped]);

  const prefix = currency === "BRL" ? "R$" : "$";

  const counts = useMemo(() => {
    const acc = { green: 0, blue: 0, red: 0, none: 0 };
    for (const r of rows) {
      if (r.color === "green") acc.green++;
      else if (r.color === "blue") acc.blue++;
      else if (r.color === "red") acc.red++;
      else acc.none++;
    }
    return acc;
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="colorization-chart">
      <div className="flex flex-wrap items-center gap-4 text-[11px] font-mono-tab">
        <div className="text-muted-foreground uppercase tracking-[0.18em]">
          N (SMA · dias) = <span className="text-foreground font-semibold">{nClamped}</span>
          <span className="opacity-70 ml-1">(vindo da Média 1)</span>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="color-med-line"
            checked={showMedLine}
            onCheckedChange={(v) => setShowMedLine(!!v)}
            data-testid="colorization-toggle-med"
          />
          <Label
            htmlFor="color-med-line"
            className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground cursor-pointer"
          >
            Exibir linha da média (hi/lo médios)
          </Label>
        </div>
      </div>

      <div className="h-[460px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="oklch(0.9 0.005 285.938)" vertical={false} />
            <XAxis
              dataKey="raw_date"
              ticks={ticks}
              tickFormatter={monthYearLabel}
              interval={0}
              tick={{ fontSize: 10 }}
              stroke={COLORS.gray}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              stroke={COLORS.gray}
              tickFormatter={(v) => `${prefix} ${Number(v).toFixed(2)}`}
              width={75}
              domain={yDomain}
              allowDataOverflow={false}
            />
            <Tooltip
              content={<ColorizationTooltip prefix={prefix} field={field} />}
              cursor={{ stroke: COLORS.gray, strokeDasharray: "2 4" }}
            />
            <Legend
              verticalAlign="bottom"
              height={34}
              wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
              content={() => (
                <ColorizationLegend
                  fieldLabel={FIELD_LABEL[field] || "Preço"}
                  N={nClamped}
                  showMed={showMedLine}
                />
              )}
            />

            {/* Gray line driven by the "Momento" filter — same reference as Tendência Customizada */}
            <Line
              type="monotone"
              dataKey="line_value"
              name={FIELD_LABEL[field] || "Preço"}
              stroke={COLORS.gray}
              strokeWidth={1.6}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
              legendType="none"
            />

            {/* Trailing med series (hi/lo SMA average), optional */}
            {showMedLine && (
              <Line
                type="monotone"
                dataKey="med"
                name={`Média (SMA ${nClamped}d · hi×lo)`}
                stroke={COLORS.black}
                strokeWidth={1}
                strokeDasharray="3 3"
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
                legendType="none"
              />
            )}

            {/* Scatter markers at the LOW of each candle, coloured by regime.
                Default marker is ~36 px²; 30% smaller ≈ 25. */}
            <Scatter
              name="Alta (verde)"
              dataKey="marker_green"
              fill={COLORS.green}
              shape={SmallDot}
              isAnimationActive={false}
              legendType="none"
            />
            <Scatter
              name="Lateral/indef. (azul)"
              dataKey="marker_blue"
              fill={COLORS.blue}
              shape={SmallDot}
              isAnimationActive={false}
              legendType="none"
            />
            <Scatter
              name="Baixa (vermelho)"
              dataKey="marker_red"
              fill={COLORS.red}
              shape={SmallDot}
              isAnimationActive={false}
              legendType="none"
            />
            {/* Hidden reference line keeps axes consistent even on tiny ranges */}
            <ReferenceLine y={0} stroke="transparent" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono-tab">
        <SummaryCell color={COLORS.green} label="Alta" count={counts.green} />
        <SummaryCell color={COLORS.blue} label="Lateral/indef." count={counts.blue} />
        <SummaryCell color={COLORS.red} label="Baixa" count={counts.red} />
        <SummaryCell color={COLORS.gray} label="Sem dados (j < N)" count={counts.none} />
      </div>
    </div>
  );
}

function SummaryCell({ color, label, count }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
      <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">
          {label}
        </div>
        <div className="font-semibold">{count} candles</div>
      </div>
    </div>
  );
}

function ColorizationLegend({ fieldLabel, N, showMed }) {
  const entries = [
    { kind: "line", color: COLORS.gray, dashed: false, label: `${fieldLabel} · Momento` },
    ...(showMed
      ? [{ kind: "line", color: COLORS.black, dashed: true, label: `Média SMA ${N}d (hi × lo)` }]
      : []),
    { kind: "dot", color: COLORS.green, label: "Alta (verde)" },
    { kind: "dot", color: COLORS.blue, label: "Lateral/indef. (azul)" },
    { kind: "dot", color: COLORS.red, label: "Baixa (vermelho)" },
  ];
  return (
    <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 pt-2" data-testid="colorization-legend">
      {entries.map((e, i) => (
        <li key={i} className="flex items-center gap-2 text-[11px] text-foreground/85">
          {e.kind === "line" ? (
            <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
              <line
                x1="1"
                x2="21"
                y1="5"
                y2="5"
                stroke={e.color}
                strokeWidth={e.dashed ? 1 : 1.6}
                strokeDasharray={e.dashed ? "4 3" : "0"}
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <span
              className="inline-block rounded-full"
              style={{ width: 7, height: 7, backgroundColor: e.color }}
            />
          )}
          <span className="whitespace-nowrap">{e.label}</span>
        </li>
      ))}
    </ul>
  );
}

function ColorizationTooltip({ active, payload, label, prefix, field }) {
  if (!active || !payload || payload.length === 0) return null;
  const r = payload[0]?.payload;
  if (!r) return null;
  const color = r.color;
  const colorLabel =
    color === "green"
      ? "VERDE · alta"
      : color === "red"
        ? "VERMELHO · baixa"
        : color === "blue"
          ? "AZUL · lateral"
          : "sem cor (dados insuficientes)";
  const colorToneClass =
    color === "green"
      ? "text-buy-strong"
      : color === "red"
        ? "text-sell-strong"
        : color === "blue"
          ? "text-blue-700"
          : "text-muted-foreground";
  return (
    <div className="rounded-md border bg-background px-3 py-2 shadow-md text-xs">
      <div className="font-semibold mb-1 font-mono-tab">
        j={r.j} · {fullDateLabel(label)}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono-tab">
        <span className="text-muted-foreground">Abertura</span>
        <span className="text-black">{prefix} {Number(r.open).toFixed(4)}</span>
        <span className="text-muted-foreground">Máxima</span>
        <span>{prefix} {Number(r.high).toFixed(4)}</span>
        <span className="text-muted-foreground">Mínima</span>
        <span>{prefix} {Number(r.low).toFixed(4)}</span>
        <span className="text-muted-foreground">Fechamento</span>
        <span>{prefix} {Number(r.close).toFixed(4)}</span>
      </div>
      <div className="mt-2 pt-2 border-t border-border font-mono-tab space-y-0.5">
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">med[j−1]</span>
          <span>{r.med_prev != null ? `${prefix} ${Number(r.med_prev).toFixed(4)}` : "—"}</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">hi[j−2]</span>
          <span>{r.hi_jm2 != null ? `${prefix} ${Number(r.hi_jm2).toFixed(4)}` : "—"}</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">low[j−2]</span>
          <span>{r.low_jm2 != null ? `${prefix} ${Number(r.low_jm2).toFixed(4)}` : "—"}</span>
        </div>
      </div>
      <div className={`mt-2 pt-2 border-t border-border text-[11px] font-semibold ${colorToneClass}`}>
        {colorLabel}
      </div>
    </div>
  );
}
